import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { API } from "@/lib/api";
import axios from "axios";
import { ShieldCheck, CheckCircle, WarningCircle } from "@phosphor-icons/react";

/**
 * Compact iframe-embeddable widget.
 * Recommended container: 380 × 120.
 * Route: /embed/certificate/:token
 */
export default function EmbedCertificate() {
  const { token } = useParams();
  const [cert, setCert] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/public/certificate/${token}`);
        if (!cancelled) setCert(r.data);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.status === 410 ? "Certificate no longer valid" : "Not found");
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const parent = window.location.origin;
  const fullUrl = `${parent}/certificate/${token}`;

  if (err) {
    return (
      <div style={styles.wrap}>
        <div style={styles.card}>
          <div style={{ ...styles.title, color: "#9BA3A0" }}>Certificate unavailable</div>
          <div style={{ fontSize: 11, color: "#6b7370", marginTop: 4 }}>{err}</div>
        </div>
      </div>
    );
  }

  if (!cert) {
    return (
      <div style={styles.wrap}>
        <div style={{ ...styles.card, opacity: 0.6 }}>
          <div style={styles.title}>Loading…</div>
        </div>
      </div>
    );
  }

  const p = cert.payload;
  const scope = p.scope;
  const score = p.compliance_score;
  let color = "#E63946";
  if (score >= 90) color = "#00A86B";
  else if (score >= 70) color = "#FFB020";
  const label = scope?.type === "single_grant" ? scope.programme : "Organisation-wide";

  return (
    <a
      href={fullUrl}
      target="_blank"
      rel="noreferrer"
      style={{ ...styles.wrap, textDecoration: "none", cursor: "pointer" }}
      data-testid="embed-card"
    >
      <div style={styles.card}>
        <div style={styles.left}>
          <div style={styles.brand}>
            <div style={styles.shieldWrap}>
              <ShieldCheck size={16} weight="duotone" color="#050B0A" />
            </div>
            <div>
              <div style={styles.brandLabel}>PerCen360</div>
              <div style={styles.brandSub}>Compliance Verified</div>
            </div>
          </div>
          <div style={styles.org} title={p.org_name}>{p.org_name}</div>
          <div style={styles.scope}>{label} · {p.receipts_total} receipts</div>
        </div>
        <div style={styles.right}>
          <div style={{ ...styles.score, color }}>{score}%</div>
          <div style={styles.badge}>
            {score >= 90
              ? <CheckCircle size={12} weight="fill" color="#00A86B" />
              : <WarningCircle size={12} weight="fill" color="#FFB020" />}
            <span>Verified</span>
          </div>
          <div style={styles.hash}>#{cert.audit_hash?.slice(0, 8)}</div>
        </div>
      </div>
    </a>
  );
}

const styles = {
  wrap: {
    display: "block",
    width: "100%",
    maxWidth: 380,
    height: 120,
    fontFamily: "'Manrope','Segoe UI',system-ui,sans-serif",
    color: "#F3F5F4",
  },
  card: {
    height: "100%",
    background: "linear-gradient(135deg, #050B0A 0%, #0A1311 100%)",
    border: "1px solid rgba(255,176,32,0.25)",
    borderRadius: 12,
    padding: 14,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "stretch",
    boxShadow: "0 8px 24px -8px rgba(0,0,0,0.5)",
    boxSizing: "border-box",
  },
  left: { display: "flex", flexDirection: "column", justifyContent: "space-between", minWidth: 0, flex: 1 },
  brand: { display: "flex", alignItems: "center", gap: 8 },
  shieldWrap: {
    width: 22, height: 22, background: "#FFB020", borderRadius: 6,
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  brandLabel: { fontSize: 11, fontWeight: 700, letterSpacing: "0.02em", lineHeight: 1 },
  brandSub: { fontSize: 9, letterSpacing: "0.18em", textTransform: "uppercase", color: "rgba(255,255,255,0.5)", marginTop: 2 },
  org: { fontSize: 15, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  scope: { fontSize: 10, color: "rgba(255,255,255,0.55)", letterSpacing: "0.04em" },
  right: { textAlign: "right", display: "flex", flexDirection: "column", justifyContent: "space-between", alignItems: "flex-end", marginLeft: 12 },
  score: { fontSize: 28, fontWeight: 800, lineHeight: 1, fontFamily: "'JetBrains Mono',ui-monospace,monospace" },
  badge: {
    display: "flex", alignItems: "center", gap: 4,
    background: "rgba(0,168,107,0.12)",
    border: "1px solid rgba(0,168,107,0.35)",
    color: "#00A86B",
    fontSize: 9, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase",
    padding: "3px 7px", borderRadius: 999,
  },
  hash: { fontSize: 9, fontFamily: "'JetBrains Mono',ui-monospace,monospace", color: "rgba(255,255,255,0.35)" },
};
